import type { FileUploadResult } from '@innovationnorway/cl-react';

type ExcelFileUploadOptions<T> = {
  importFile: (file: File) => Promise<T[]>;
  onData: (
    data: T[],
    file: File,
    isCurrent: () => boolean,
  ) => FileUploadResult | void | Promise<FileUploadResult | void>;
  onFileRemoved: () => void;
  onParseError: (file: File) => FileUploadResult;
  onParseStart?: (file: File) => void;
  onDataError?: (file: File) => FileUploadResult;
  isCurrent?: (file: File) => boolean;
};

type ImportResult<T> = { data: T[] } | { error: true };
type FileFailure = { file: File; result: FileUploadResult };

async function importFileSafely<T>(
  importFile: (file: File) => Promise<T[]>,
  file: File,
): Promise<ImportResult<T>> {
  try {
    return { data: await importFile(file) };
  } catch {
    return { error: true };
  }
}

function createDataErrorResult(
  error: unknown,
  file: File,
  isSingleFile: boolean,
  onDataError?: (file: File) => FileUploadResult,
): FileUploadResult {
  if (onDataError) return onDataError(file);
  if (isSingleFile) throw error;

  return {
    status: false,
    message: error instanceof Error ? error.message : String(error),
  };
}

async function applyData<T>(
  data: T[],
  file: File,
  isCurrent: () => boolean,
  isSingleFile: boolean,
  onData: ExcelFileUploadOptions<T>['onData'],
  onDataError?: ExcelFileUploadOptions<T>['onDataError'],
): Promise<FileUploadResult | void> {
  try {
    return await onData(data, file, isCurrent);
  } catch (error) {
    if (!isCurrent()) return undefined;
    return createDataErrorResult(error, file, isSingleFile, onDataError);
  }
}

function summarizeFailures(failures: FileFailure[]): FileUploadResult | undefined {
  if (failures.length === 0) return undefined;

  const failedFiles: string[] = [];
  const messages: string[] = [];
  for (const { file, result } of failures) {
    failedFiles.push(file.name, ...(result.failedFiles ?? []));
    if (result.message) messages.push(`${file.name}: ${result.message}`);
  }

  return {
    status: false,
    ...(messages.length > 0 ? { message: messages.join('\n') } : {}),
    failedFiles,
  };
}

export function createExcelFileUploadHandler<T>({
  importFile,
  onData,
  onFileRemoved,
  onParseError,
  onParseStart,
  onDataError,
  isCurrent: isFileCurrent,
}: ExcelFileUploadOptions<T>): (files: File[]) => Promise<FileUploadResult | undefined> {
  let generation = 0;

  return async (files) => {
    const requestGeneration = ++generation;
    if (files.length === 0) {
      onFileRemoved();
      return undefined;
    }

    const failures: FileFailure[] = [];
    let singleFileResult: FileUploadResult | undefined;

    for (const file of files) {
      onParseStart?.(file);
      const isCurrent = () =>
        requestGeneration === generation && (isFileCurrent?.(file) ?? true);
      const imported = await importFileSafely(importFile, file);
      if (!isCurrent()) return undefined;

      const result = 'error' in imported
        ? onParseError(file)
        : await applyData(
            imported.data,
            file,
            isCurrent,
            files.length === 1,
            onData,
            onDataError,
          );

      if (!isCurrent()) return undefined;
      singleFileResult = result ?? undefined;
      if (result && !result.status) failures.push({ file, result });
    }

    return files.length === 1 ? singleFileResult : summarizeFailures(failures);
  };
}
